<template>
  <div class="landing-workspace space-y-4">
    <!-- No workspace yet: the account owner creates it. -->
    <div v-if="!workspaceId" class="card p-6 space-y-4 max-w-2xl" data-test="create-workspace">
      <div>
        <h2 class="text-lg font-semibold" style="color: var(--text-primary);">
          Set up landing pages for your business
        </h2>
        <p class="text-sm mt-1" style="color: var(--text-secondary);">
          We use these details on your pages and to route leads. You can change them later.
        </p>
      </div>
      <form class="grid gap-3 sm:grid-cols-2" @submit.prevent="createWorkspace">
        <label class="flex flex-col text-sm sm:col-span-2">
          <span class="font-medium" style="color: var(--text-secondary);">Business name</span>
          <input v-model="setup.name" class="form-input mt-1" type="text" maxlength="255" required data-field="business-name">
        </label>
        <label class="flex flex-col text-sm">
          <span class="font-medium" style="color: var(--text-secondary);">Business phone</span>
          <input
            v-model="setup.phone"
            class="form-input mt-1"
            type="tel"
            required
            placeholder="+12125550123"
            data-field="business-phone"
            aria-describedby="setup-phone-hint">
          <span id="setup-phone-hint" class="text-xs mt-1" style="color: var(--text-secondary);">US number, with +1.</span>
        </label>
        <label class="flex flex-col text-sm">
          <span class="font-medium" style="color: var(--text-secondary);">Service areas (optional)</span>
          <input v-model="setup.areas" class="form-input mt-1" type="text" placeholder="Brookfield, Riverside" data-field="service-areas">
        </label>
        <p v-for="entry in setupErrors" :key="entry" class="text-sm text-red-700 sm:col-span-2" role="alert">
          {{ entry }}
        </p>
        <div class="sm:col-span-2">
          <button type="submit" class="btn btn-primary" data-action="create-workspace" :disabled="setupBusy">
            {{ setupBusy ? 'Setting up…' : 'Continue' }}
          </button>
        </div>
      </form>
    </div>

    <div v-else-if="state === 'loading'" class="text-body" style="color: var(--text-secondary);">
      Loading landing pages…
    </div>
    <div v-else-if="state === 'denied'" class="card p-6" role="alert" data-test="denied">
      <p class="font-medium" style="color: var(--text-primary);">
        This landing page workspace isn't available to you.
      </p>
      <p class="text-sm mt-1" style="color: var(--text-secondary);">
        Only the account owner can manage landing pages. Ask your account owner for access.
      </p>
    </div>
    <div v-else-if="state === 'error'" class="rounded-lg border border-red-300 p-4 text-sm text-red-700" role="alert" data-test="load-error">
      Landing pages couldn't be loaded. Check your connection and try again.
      <button type="button" class="underline ml-1" data-action="retry" @click="load">
        Try again
      </button>
    </div>

    <template v-else-if="workspace">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <p class="text-sm" style="color: var(--text-secondary);">
          {{ workspace.name }}<span v-if="readOnly"> · view only</span>
        </p>
      </div>
      <nav class="flex gap-1 border-b overflow-x-auto" style="border-color: var(--border-primary, #e5e7eb);" aria-label="Landing page sections">
        <button
          v-for="tab in TABS"
          :key="tab.key"
          type="button"
          class="px-3 py-2 text-sm font-medium whitespace-nowrap"
          :class="activeTab === tab.key ? 'border-b-2 border-current' : ''"
          :aria-current="activeTab === tab.key ? 'page' : undefined"
          :data-tab="tab.key"
          @click="selectTab(tab.key)">
          {{ tab.label }}
        </button>
      </nav>

      <section v-if="activeTab === 'pages'" aria-label="Pages">
        <template v-if="openPageId">
          <div class="mb-3">
            <button type="button" class="text-sm underline" data-action="back-to-library" @click="closeEditor">
              ← All pages
            </button>
          </div>
          <div v-if="pageState === 'loading'" class="text-sm" style="color: var(--text-secondary);">
            Loading page…
          </div>
          <div v-else-if="pageState === 'error'" class="text-sm text-red-700" role="alert">
            This page couldn't be loaded.
            <button type="button" class="underline" @click="openPage(openPageId)">
              Try again
            </button>
          </div>
          <PageEditor
            v-else-if="page"
            :key="`${page.id}-${editorGeneration}`"
            :page="page"
            :save-draft="saveDraft"
            :change-path="changePath"
            :request-preview="requestPreview"
            :upload-asset="uploadAsset"
            :list-assets="listAssets"
            :read-only="readOnly"
            @saved="adoptPage"
            @reload="reloadPage"
            @publish="publishing = true" />
          <PublishDialog
            v-if="publishing && page"
            :api="api"
            :page="page"
            :call-settings-url="endpoints.call_settings || ''"
            @published="publishedPage"
            @close="publishing = false" />
        </template>
        <PageLibrary
          v-else
          :key="libraryGeneration"
          :api="api"
          :workspace="workspace"
          :read-only="readOnly"
          @open="openPage"
          @changed="load()" />
      </section>

      <section v-else-if="activeTab === 'domains'" aria-label="Domains" class="space-y-3">
        <div v-if="!workspace.sites.length" class="card p-6 text-sm" style="color: var(--text-secondary);">
          Create a site on the Pages tab first; its Made.ly address and domains appear here.
        </div>
        <template v-else>
          <label v-if="workspace.sites.length > 1" class="flex flex-col text-sm max-w-sm">
            <span class="font-medium" style="color: var(--text-secondary);">Site</span>
            <select v-model="domainSiteId" class="form-select mt-1" @change="loadSite">
              <option v-for="choice in workspace.sites" :key="choice.id" :value="choice.id">{{ choice.name }}</option>
            </select>
          </label>
          <div v-if="siteState === 'loading'" class="text-sm" style="color: var(--text-secondary);">
            Loading domains…
          </div>
          <div v-else-if="siteState === 'error'" class="text-sm text-red-700" role="alert">
            Domains couldn't be loaded.
            <button type="button" class="underline" @click="loadSite">
              Try again
            </button>
          </div>
          <DomainSettings v-else-if="site" :key="site.id + site.domain_version" :api="api" :site="site" :read-only="readOnly" @changed="loadSite" />
        </template>
      </section>

      <section v-else-if="activeTab === 'leads'" aria-label="Leads">
        <LeadInbox :workspace-id="workspaceId" :csrf-token="csrfToken" />
      </section>

      <section v-else-if="activeTab === 'calls'" aria-label="Call and lead settings">
        <CallSettings :workspace-id="workspaceId" :csrf-token="csrfToken" />
      </section>

      <section v-else-if="activeTab === 'business'" aria-label="Business settings" class="card p-4 space-y-3 max-w-2xl">
        <h2 class="font-semibold" style="color: var(--text-primary);">
          Business details
        </h2>
        <form class="grid gap-3 sm:grid-cols-2" @submit.prevent="saveBusiness">
          <label class="flex flex-col text-sm sm:col-span-2">
            <span class="font-medium" style="color: var(--text-secondary);">Business name</span>
            <input v-model="business.name" class="form-input mt-1" type="text" maxlength="255" required :disabled="readOnly" data-field="settings-name">
          </label>
          <label class="flex flex-col text-sm">
            <span class="font-medium" style="color: var(--text-secondary);">Business phone</span>
            <input v-model="business.phone" class="form-input mt-1" type="tel" :disabled="readOnly" data-field="settings-phone">
          </label>
          <label class="flex flex-col text-sm">
            <span class="font-medium" style="color: var(--text-secondary);">Service areas</span>
            <input v-model="business.areas" class="form-input mt-1" type="text" :disabled="readOnly" data-field="settings-areas">
          </label>
          <p class="text-xs sm:col-span-2" style="color: var(--text-secondary);">
            Lead notification recipients are managed in Call and lead settings.
          </p>
          <p v-for="entry in businessErrors" :key="entry" class="text-sm text-red-700 sm:col-span-2" role="alert">
            {{ entry }}
          </p>
          <div v-if="!readOnly" class="sm:col-span-2">
            <button type="submit" class="btn btn-primary" :disabled="businessBusy" data-action="save-business">
              {{ businessBusy ? 'Saving…' : 'Save details' }}
            </button>
          </div>
        </form>
      </section>
      <p role="status" aria-live="polite" class="text-sm" :class="message?.kind === 'success' ? 'text-green-700' : 'text-red-700'">
        {{ message ? message.text : '' }}
      </p>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { createLandingApi, listAllAssets, waitForOperation } from '~/javascript/services/landing_pages/api'
import LeadInbox from './lead_inbox.vue'
import CallSettings from './call_settings.vue'
import PageLibrary from './PageLibrary.vue'
import PageEditor from './PageEditor.vue'
import PublishDialog from './PublishDialog.vue'
import DomainSettings from './DomainSettings.vue'

// Receives only the authorized workspace id and native endpoint metadata;
// everything else is read from the session/CSRF Ajax API.
const props = defineProps({
  workspaceId: { type: String, default: '' },
  endpoints: { type: Object, default: () => ({}) },
  csrfToken: { type: String, default: '' },
  api: { type: Object, default: null }
})

const TABS = [
  { key: 'pages', label: 'Pages' },
  { key: 'domains', label: 'Addresses and domains' },
  { key: 'leads', label: 'Leads' },
  { key: 'calls', label: 'Call and lead settings' },
  { key: 'business', label: 'Business details' }
]

const api = props.api || createLandingApi({ endpoints: props.endpoints, csrfToken: props.csrfToken })
const workspace = ref(null)
const state = ref(props.workspaceId ? 'loading' : 'create')
const activeTab = ref(initialTab())
const openPageId = ref(null)
const page = ref(null)
const pageState = ref('idle')
const editorGeneration = ref(0)
const libraryGeneration = ref(0)
const publishing = ref(false)
const domainSiteId = ref('')
const site = ref(null)
const siteState = ref('idle')
const message = ref(null)
const setup = reactive({ name: '', phone: '', areas: '' })
const setupBusy = ref(false)
const setupErrors = ref([])
const business = reactive({ name: '', phone: '', areas: '' })
const businessBusy = ref(false)
const businessErrors = ref([])
const readOnly = computed(() => workspace.value?.permissions?.manage !== true)

function initialTab() {
  const tab = new URLSearchParams(globalThis.location?.search || '').get('tab')
  return TABS.some((item) => item.key === tab) ? tab : 'pages'
}

function areas(text) {
  return text.split(',').map((item) => item.trim()).filter(Boolean)
}

function errorList(error) {
  const fields = Object.entries(error?.fields || {}).flatMap(([key, messages]) => messages.map((entry) => `${key.replace(/^business\./, '')} ${entry}`))
  return fields.length ? fields : ['The change could not be saved. Try again.']
}

async function load({ keepBusinessInput = false } = {}) {
  if (!props.workspaceId) return
  try {
    const result = await api.workspace()
    workspace.value = result.data
    if (!keepBusinessInput) {
      Object.assign(business, { name: result.data.name || '',
        phone: result.data.business?.phone || '',
        areas: (result.data.business?.service_areas || []).join(', ') })
    }
    if (!domainSiteId.value && result.data.sites.length) domainSiteId.value = result.data.sites[0].id
    libraryGeneration.value += 1
    state.value = 'ready'
  } catch (error) {
    state.value = error.status === 404 ? 'denied' : 'error'
  }
}

async function createWorkspace() {
  setupBusy.value = true
  setupErrors.value = []
  const body = { name: setup.name.trim(), business: { phone: setup.phone.trim() } }
  if (setup.areas.trim()) body.business.service_areas = areas(setup.areas)
  try {
    await api.createWorkspace(body)
    globalThis.location?.reload()
  } catch (error) {
    setupErrors.value = error.code === 'owner_required' ?
      ['Only the account owner can set up landing pages.'] :
      errorList(error)
  } finally {
    setupBusy.value = false
  }
}

function selectTab(key) {
  activeTab.value = key
  if (key === 'domains') loadSite()
}

async function openPage(pageId) {
  openPageId.value = pageId
  pageState.value = 'loading'
  try {
    const result = await api.page(pageId)
    page.value = result.data
    pageState.value = 'ready'
  } catch (error) {
    pageState.value = 'error'
  }
}

function closeEditor() {
  openPageId.value = null
  page.value = null
  publishing.value = false
}

function adoptPage(saved) {
  page.value = saved
}

// Explicit "load the latest version": the editor is rebuilt from the server.
async function reloadPage() {
  await openPage(openPageId.value)
  editorGeneration.value += 1
}

async function publishedPage() {
  const result = await api.page(page.value.id)
  page.value = result.data
  editorGeneration.value += 1
  message.value = { kind: 'success', text: 'Published' }
}

const saveDraft = (payload, version) => api.saveDraft(page.value.id, payload, version)
const changePath = (path, version) => api.changePath(page.value.id, path, version)

async function requestPreview(draftVersion) {
  const queued = await api.preview(page.value.id, draftVersion, page.value.version)
  const operation = await waitForOperation(api, queued.data.id, { intervalMs: 1500, attempts: 80 })
  if (operation?.state !== 'succeeded') throw Object.assign(new Error('preview_failed'), { code: operation?.error?.code || 'preview_failed' })
  return operation.result
}

async function uploadAsset(file) {
  const result = await api.uploadAsset(page.value.id, file, page.value.version)
  return result.data.id
}

const listAssets = () => listAllAssets(api, page.value.id)

async function loadSite() {
  if (!domainSiteId.value) return
  siteState.value = 'loading'
  try {
    const result = await api.site(domainSiteId.value)
    site.value = result.data
    siteState.value = 'ready'
  } catch (error) {
    siteState.value = 'error'
  }
}

async function saveBusiness() {
  businessBusy.value = true
  businessErrors.value = []
  message.value = null
  const changes = { phone: business.phone.trim() || null, service_areas: areas(business.areas) }
  try {
    const result = await api.updateWorkspace({ name: business.name.trim(), business: changes }, workspace.value.version)
    workspace.value = { ...workspace.value, ...result.data }
    message.value = { kind: 'success', text: 'Business details saved' }
  } catch (error) {
    if (error.code === 'version_conflict') {
      // Like the editor: keep the owner's input; saving again replaces the newer details.
      await load({ keepBusinessInput: true })
      businessErrors.value = ['These details changed elsewhere. Your changes are kept here; save again to replace them.']
    } else {
      businessErrors.value = errorList(error)
    }
  } finally {
    businessBusy.value = false
  }
}

onMounted(load)
</script>
